import React, { useState, useEffect } from 'react';
import { Heart, Share2, MessageCircle, Send, X } from 'lucide-react';
import Navbar from './Components/Navbar';
import Footer from '../Components/Footer';
import PageHeroBanner from './Components/PageHeroBanner';
import { apiUrl } from '../api';
import './EventsPage.css';

// Turn URLs / www links inside description text into clickable links.
const LINK_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
function linkify(text) {
  if (!text) return text;
  const parts = String(text).split(LINK_RE);
  return parts.map((part, i) => {
    if (!part) return null;
    if (LINK_RE.test(part)) {
      LINK_RE.lastIndex = 0; // reset after test (regex has /g)
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          className="event-link"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

// Map an API listing -> the shape EventCard expects (single admin photo)
const listingToEvent = (l) => ({
  id: l._id,
  title: l.title,
  date: l.date || 'TBA',
  time: l.time || '-',
  location: l.location || '-',
  image: (l.images && l.images[0]) || '/images.png',
  description: l.description || '',
  link: l.link || '',
  tags: l.tags && l.tags.length ? l.tags : ['Event'],
  likes: 0,
  comments: [],
  fromDb: true,
});

// Map an API listing -> the shape the club card expects (single admin photo)
const listingToClub = (l) => ({
  id: l._id,
  name: l.title,
  desc: l.description || '',
  meets: l.time || l.date || 'See details',
  location: l.location || '-',
  image: (l.images && l.images[0]) || '',
  link: l.link || '',
});

// Lightweight "x ago" formatter for comment timestamps.
function timeAgo(iso) {
  if (!iso) return 'Just now';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return ''; }
}

const STATIC_EVENTS = [
  {
    id: 'ev1',
    title: 'Chai & Connect - Glasgow Workshop',
    date: 'April 12, 2026',
    time: '2:00 PM - 4:00 PM',
    location: 'Glasgow City Centre',
    image: '/chaipecharchatea2.jpeg',
    description: 'Join us for a hands-on karak chai making workshop. Learn the ancient art of brewing the perfect cup, discover the wellness benefits of traditional spices, and leave with your own chai kit. Perfect for teams, hen parties, and curious souls.',
    tags: ['Workshop', 'Chai', 'Wellness'],
    likes: 34,
    comments: [
      { id: 1, name: 'Priya S.', text: 'Attended the last one - absolutely loved it! The spice knowledge shared was incredible.', time: '2 days ago' },
      { id: 2, name: 'Fatima A.', text: 'Can\'t wait for this! Bringing my whole team from work.', time: '1 day ago' },
      { id: 3, name: 'James M.', text: 'Is this suitable for beginners? Never made chai from scratch before.', time: '5 hours ago' },
    ],
  },
  {
    id: 'ev2',
    title: 'Pop-Up Market - Elaichi & Love Stall',
    date: 'April 19, 2026',
    time: '10:00 AM - 6:00 PM',
    location: 'Merchant City, Glasgow',
    image: '/Popandbloomsnack1.jpeg',
    description: 'Find us at the Merchant City Spring Market! Sample our full range - chai balls, lotus seed snacks, Berry Banger shots, and exclusive gift boxes. First 50 customers get a free Sticky Soul sample.',
    tags: ['Market', 'Pop-Up', 'Tasting'],
    likes: 52,
    comments: [
      { id: 1, name: 'Aisha K.', text: 'Will there be gift boxes available to buy on the day?', time: '3 days ago' },
      { id: 2, name: 'Tom H.', text: 'Came to the last pop-up and the Berry Banger shots are something else!', time: '1 day ago' },
    ],
  },
  {
    id: 'ev3',
    title: 'Monthly Discovery Box - April Edition',
    date: 'April 25, 2026',
    time: 'Dispatched all week',
    location: 'UK-wide Delivery',
    image: '/discoverybox2.jpeg',
    description: 'April\'s Discovery Box theme is "Spring Reset" - new seasonal chai blends, a limited-edition snack, two Berry Bangers, and an exclusive wellness journal. Subscribe by April 20th to guarantee yours.',
    tags: ['Subscription', 'Delivery', 'Exclusive'],
    likes: 89,
    comments: [
      { id: 1, name: 'Sara B.', text: 'Been subscribing for 3 months - each box gets better. Spring Reset sounds amazing!', time: '4 days ago' },
      { id: 2, name: 'Nadia R.', text: 'Do you ship to Edinburgh?', time: '2 days ago' },
      { id: 3, name: 'Marcus T.', text: 'The wellness journal addition is a brilliant touch.', time: '12 hours ago' },
    ],
  },
  {
    id: 'ev4',
    title: 'Corporate Wellness Day - Chai & Mindfulness',
    date: 'May 3, 2026',
    time: '11:00 AM - 1:00 PM',
    location: 'Your Office (Glasgow)',
    image: '/weeklyritualbox1.jpeg',
    description: 'Bring Elaichi & Love to your workplace. We\'ll run a 2-hour chai making and mindfulness session for your team - spice blending, breathing exercises, and a take-home chai kit for everyone. Enquire for group pricing.',
    tags: ['Corporate', 'Wellness', 'Team Building'],
    likes: 21,
    comments: [
      { id: 1, name: 'Claire D.', text: 'We did this last quarter and the team still talks about it. Highly recommend!', time: '1 week ago' },
    ],
  },
];

function EventCard({ event }) {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(event.likes);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState(event.comments);
  const [newComment, setNewComment] = useState('');
  const [newName, setNewName] = useState('');
  const [shareToast, setShareToast] = useState(false);

  // Load persisted comments for DB-backed events.
  useEffect(() => {
    if (!event.fromDb) return;
    let active = true;
    (async () => {
      try {
        const res = await fetch(apiUrl(`/api/collections/listings/${event.id}/comments`));
        const data = await res.json();
        if (active && data.success) {
          setComments(data.comments.map(c => ({ id: c._id, name: c.name, text: c.text, time: timeAgo(c.createdAt) })));
        }
      } catch { /* keep whatever is there */ }
    })();
    return () => { active = false; };
  }, [event.id, event.fromDb]);

  const handleLike = () => {
    setLiked(prev => !prev);
    setLikeCount(prev => liked ? prev - 1 : prev + 1);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setShareToast(true);
    setTimeout(() => setShareToast(false), 2500);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    const name = newName.trim() || 'Anonymous';
    const text = newComment.trim();
    setNewComment('');
    setNewName('');

    if (event.fromDb) {
      try {
        const res = await fetch(apiUrl(`/api/collections/listings/${event.id}/comments`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, text }),
        });
        const data = await res.json();
        if (data.success) {
          setComments(prev => [...prev, { id: data.comment._id, name: data.comment.name, text: data.comment.text, time: 'Just now' }]);
          return;
        }
      } catch { /* fall back to local append */ }
    }
    setComments(prev => [...prev, { id: Date.now(), name, text, time: 'Just now' }]);
  };

  return (
    <div className="event-card">
      <div className="event-image-wrap">
        <img src={event.image} alt={event.title} className="event-image" onError={e => { e.target.src = '/images.png'; }} />
        <div className="event-tags">
          {event.tags.map(tag => <span key={tag} className="event-tag">{tag}</span>)}
        </div>
      </div>

      <div className="event-body">
        <div className="event-meta">
          <span className="event-date">📅 {event.date}</span>
          <span className="event-time">🕑 {event.time}</span>
          <span className="event-location">📍 {event.location}</span>
        </div>

        <h2 className="event-title">{event.title}</h2>
        <p className="event-description">{linkify(event.description)}</p>

        {event.link && (
          <a
            className="event-join"
            href={event.link.startsWith('http') ? event.link : `https://${event.link}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Join this event →
          </a>
        )}

        <div className="event-actions">
          <button className={`action-btn like-btn ${liked ? 'liked' : ''}`} onClick={handleLike}>
            <Heart size={18} fill={liked ? '#4B0213' : 'none'} color={liked ? '#4B0213' : '#391C14'} />
            <span>{likeCount}</span>
          </button>

          <button className="action-btn comment-btn" onClick={() => setCommentsOpen(o => !o)}>
            <MessageCircle size={18} color="#391C14" />
            <span>{comments.length}</span>
          </button>

          <button className="action-btn share-btn" onClick={handleShare}>
            <Share2 size={18} color="#391C14" />
            <span>Share</span>
            {shareToast && <div className="share-toast">Link copied!</div>}
          </button>
        </div>

        {commentsOpen && (
          <div className="comments-section">
            <div className="comments-list">
              {comments.map(c => (
                <div key={c.id} className="comment">
                  <div className="comment-avatar">{c.name[0]}</div>
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-name">{c.name}</span>
                      <span className="comment-time">{c.time}</span>
                    </div>
                    <p className="comment-text">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="comment-input-area">
              <input
                className="comment-name-input"
                placeholder="Your name"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
              <div className="comment-input-row">
                <input
                  className="comment-input"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddComment()}
                />
                <button className="comment-send-btn" onClick={handleAddComment}>
                  <Send size={16} color="white" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const CLUBS = [
  {
    id: 'cl1',
    name: 'Chai Pe Charcha Circle',
    desc: 'A monthly gathering over chai where we talk about everything - from food and culture to politics, wellness, and community. Open to all. No agenda. Just real conversations.',
    meets: 'Every 2nd Saturday',
    location: 'Glasgow City Centre',
    icon: '🫖',
    link: '#chai-pe-charcha',
  },
  {
    id: 'cl2',
    name: 'Elaichi Wellness Club',
    desc: 'A community of people committed to living well - through Ayurvedic wisdom, mindful eating, and the power of plants. Monthly meetups, recipes, and a WhatsApp community.',
    meets: 'Monthly',
    location: 'Online + Glasgow',
    icon: '🌿',
    link: '#wellness-club',
  },
  {
    id: 'cl3',
    name: 'Discovery Box Subscribers',
    desc: 'Subscribe to our monthly Discovery Box and join an exclusive community of Elaichi & Love superfans. First access to new products, member-only events, and behind-the-scenes content.',
    meets: 'Monthly (box delivery)',
    location: 'UK-wide',
    icon: '📦',
    link: '#discovery-club',
  },
  {
    id: 'cl4',
    name: 'Corporate Wellness Network',
    desc: 'A growing network of HR leads, office managers, and wellbeing champions who bring Elaichi & Love experiences to their workplaces. Join to get priority booking and group rates.',
    meets: 'Quarterly networking event',
    location: 'Glasgow',
    icon: '🏢',
    link: '/corporate-events',
  },
];

function EventsPage() {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState(STATIC_EVENTS);
  const [clubs, setClubs] = useState(CLUBS);

  useEffect(() => {
    // Pull live listings from the admin-managed collections.
    // Falls back to the static content if a collection is empty or unreachable.
    const load = async (slug, mapper, setter) => {
      try {
        const res = await fetch(apiUrl(`/api/collections/${slug}/listings`));
        if (!res.ok) return;
        const data = await res.json();
        if (data.success && Array.isArray(data.listings) && data.listings.length > 0) {
          setter(data.listings.map(mapper));
        }
      } catch {
        /* keep static fallback */
      }
    };
    load('events', listingToEvent, setEvents);
    load('clubs', listingToClub, setClubs);
  }, []);

  return (
    <>
      <Navbar />
      <div className="events-hero-wrap" style={{ marginTop: '5rem' }}>
        <PageHeroBanner
          heading="Events & Clubs"
          image="/bgimage.jpeg"
          eyebrow="Community · Connection · Chai"
          description="Workshops, pop-ups, community clubs and more - happening in your world."
        />
      </div>
      <div className="events-page">
        <div className="events-hero">
          <div className="events-tabs">
            <button
              className={`events-tab ${activeTab === 'events' ? 'events-tab--active' : ''}`}
              onClick={() => setActiveTab('events')}
            >
              Upcoming Events
            </button>
            <button
              className={`events-tab ${activeTab === 'clubs' ? 'events-tab--active' : ''}`}
              onClick={() => setActiveTab('clubs')}
            >
              Clubs & Communities
            </button>
          </div>
        </div>

        {activeTab === 'events' && (
          <div className="events-grid">
            {events.map(event => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}

        {activeTab === 'clubs' && (
          <div className="clubs-section">
            <p className="clubs-intro">Join a community built around chai, wellness, and connection. All clubs are open - find your tribe below.</p>
            <div className="clubs-grid">
              {clubs.map(club => (
                <div key={club.id} className="club-card">
                  {club.image ? (
                    <div className="club-image-wrap">
                      <img src={club.image} alt={club.name} className="club-image" onError={e => { e.target.src = '/images.png'; }} />
                    </div>
                  ) : (
                    <span className="club-icon">{club.icon || '🫖'}</span>
                  )}
                  <h3 className="club-name">{club.name}</h3>
                  <p className="club-desc">{linkify(club.desc)}</p>
                  <div className="club-meta">
                    <span>📅 {club.meets}</span>
                    <span>📍 {club.location}</span>
                  </div>
                  {club.link && club.link !== '#' && (
                    <a
                      className="club-join"
                      href={club.link.startsWith('http') ? club.link : `https://${club.link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Join →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default EventsPage;
